import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AndroidIcon from "@mui/icons-material/Android";
import WidgetsIcon from "@mui/icons-material/Widgets";
import CodeIcon from "@mui/icons-material/Code";
import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";

// ✅ App Development Content Data
const appDevContent = [
  {
    section: "Mobile Platforms",
    icon: <PhoneIphoneIcon />,
    items: [
      {
        name: "iOS Development",
        description: "Build apps for iPhone and iPad using Swift and Xcode.",
        fullContent: [
          {
            heading: "Beginner",
            points: [
              "Learn Swift syntax: variables, loops, functions",
              "Use UIKit for simple UI components",
              "Understand MVC architecture",
            ],
            code: `// Hello World in Swift
import UIKit
let greeting = "Hello iOS"
print(greeting)`,
            language: "swift"
          },
          {
            heading: "Intermediate",
            points: [
              "Use SwiftUI for declarative UI design",
              "Navigation between Views",
              "Implement TableViews and ListViews",
            ],
            code: `// SwiftUI Example
import SwiftUI
struct ContentView: View {
  var body: some View {
    Text("Hello SwiftUI").padding()
  }
}`,
            language: "swift"
          },
          {
            heading: "Advanced",
            points: [
              "Access device APIs: Camera, Location, Notifications",
              "Core Data for local database",
              "Deploy to App Store",
            ],
            code: `// Get Location Example
import CoreLocation
let manager = CLLocationManager()
manager.requestWhenInUseAuthorization()
print(manager.location?.coordinate)`,
            language: "swift"
          },
        ],
      },
      {
        name: "Android Development",
        description: "Create Android apps using Kotlin or Java.",
        fullContent: [
          {
            heading: "Beginner",
            points: [
              "Learn Kotlin syntax and basics",
              "Create simple activities and layouts",
              "Understand AndroidManifest.xml",
            ],
            code: `// Hello World in Kotlin
fun main() {
    println("Hello Android")
}`,
            language: "kotlin"
          },
          {
            heading: "Intermediate",
            points: [
              "Use RecyclerView for lists",
              "Navigation Components for screen navigation",
              "Implement simple UI with XML and Jetpack Compose",
            ],
            code: `// Jetpack Compose Example
import androidx.compose.material.Text
import androidx.compose.runtime.Composable

@Composable
fun Greeting() {
    Text("Hello Compose")
}`,
            language: "kotlin"
          },
          {
            heading: "Advanced",
            points: [
              "Access device sensors and services",
              "Use Room for local storage",
              "Publish APK / AAB to Play Store",
            ],
            code: `// Room Database Example
@Entity
data class User(@PrimaryKey val id:Int, val name:String)`,
            language: "kotlin"
          },
        ],
      },
    ],
  },
  {
    section: "Cross-Platform Frameworks",
    icon: <AndroidIcon />,
    items: [
      {
        name: "Flutter",
        description: "Cross-platform app development with Dart.",
        fullContent: [
          {
            heading: "Beginner",
            points: [
              "Learn Dart syntax",
              "Understand Stateless vs Stateful widgets",
              "Build simple UI screens",
            ],
            code: `// Hello World in Flutter
import 'package:flutter/material.dart';
void main() => runApp(MaterialApp(home: Text('Hello Flutter')));`,
            language: "dart"
          },
          {
            heading: "Intermediate",
            points: [
              "Use Stateful widgets for interactive UI",
              "Implement navigation between screens",
              "Use ListView and GridView for layouts",
            ],
            code: `// Counter App Example
class Counter extends StatefulWidget {
  @override
  _CounterState createState() => _CounterState();
}
class _CounterState extends State<Counter> {
  int count = 0;
  @override
  Widget build(BuildContext context) {
    return ElevatedButton(
      onPressed: () => setState(() => count++),
      child: Text('Count: $count'),
    );
  }
}`,
            language: "dart"
          },
          {
            heading: "Advanced",
            points: [
              "Integrate Firebase (Auth, Firestore, Analytics)",
              "Implement Push Notifications",
              "Deploy to Android and iOS from one codebase",
            ],
            code: `// Firebase Firestore Example
FirebaseFirestore.instance.collection('users').add({'name':'John'});`,
            language: "dart"
          },
        ],
      },
      {
        name: "React Native",
        description: "Build native apps using JavaScript and React.",
        fullContent: [
          {
            heading: "Beginner",
            points: [
              "Learn JSX and React basics",
              "Create simple components",
              "Use View, Text, and Button components",
            ],
            code: `import { Text, View } from 'react-native';
export default function App() {
  return (<View><Text>Hello React Native</Text></View>);
}`,
            language: "javascript"
          },
          {
            heading: "Intermediate",
            points: [
              "Use useState and useEffect hooks",
              "Implement navigation with React Navigation",
              "Use FlatList for dynamic lists",
            ],
            code: `import { useState } from 'react';
const [count, setCount] = useState(0);
// Increment count: setCount(count + 1);`,
            language: "javascript"
          },
          {
            heading: "Advanced",
            points: [
              "Connect to REST APIs",
              "Use Redux or Context API for state management",
              "Deploy to App Store and Play Store",
            ],
            code: `fetch('https://api.example.com/users')
.then(res => res.json())
.then(data => console.log(data));`,
            language: "javascript"
          },
        ],
      },
    ],
  },
  {
    section: "App Architecture & Backend",
    icon: <WidgetsIcon />,
    items: [
      {
        name: "Backend Integration",
        description: "Connect apps with REST APIs and databases.",
        fullContent: [
          {
            heading: "Beginner",
            points: [
              "Learn basic REST API calls",
              "Understand JSON response structure",
            ],
            code: `fetch('https://api.example.com/data')
.then(res => res.json())
.then(data => console.log(data));`,
            language: "javascript"
          },
          {
            heading: "Intermediate",
            points: [
              "Use Firebase Realtime Database or Firestore",
              "Integrate with MongoDB or MySQL backend",
            ],
            code: `// Firebase write example
firebase.database().ref('users').push({name:'John'});`,
            language: "javascript"
          },
          {
            heading: "Advanced",
            points: [
              "Implement Authentication & Authorization",
              "Use JWT for secure API access",
              "Handle offline sync and caching",
            ],
            code: `// JWT Fetch Example
fetch('/api/protected', {
  headers: { Authorization: 'Bearer ' + token }
})`,
            language: "javascript"
          },
        ],
      },
      {
        name: "State Management",
        description: "Manage app state efficiently.",
        fullContent: [
          {
            heading: "Beginner",
            points: [
              "Local component state with useState (React) or setState (Flutter)",
            ],
            code: `// React example
const [count, setCount] = useState(0);`,
            language: "javascript"
          },
          {
            heading: "Intermediate",
            points: [
              "Use Context API for global state",
              "Implement simple Provider pattern",
            ],
            code: `// React Context example
const MyContext = React.createContext();`,
            language: "javascript"
          },
          {
            heading: "Advanced",
            points: [
              "Use Redux or MobX for complex state",
              "Bloc pattern in Flutter",
              "Combine with API calls and caching",
            ],
            code: `// Redux example
store.dispatch({ type: 'INCREMENT' });`,
            language: "javascript"
          },
        ],
      },
    ],
  },
  {
    section: "Advanced App Features",
    icon: <CodeIcon />,
    items: [
      {
        name: "Push Notifications",
        description: "Engage users with timely updates.",
        fullContent: [
          {
            heading: "Beginner",
            points: [
              "Understand basic notification concepts",
              "Use local notifications",
            ],
            code: `// Flutter local notification example
import 'package:flutter_local_notifications/flutter_local_notifications.dart';
FlutterLocalNotificationsPlugin flnp = FlutterLocalNotificationsPlugin();`,
            language: "dart"
          },
          {
            heading: "Intermediate",
            points: [
              "Use Firebase Cloud Messaging (FCM) for push notifications",
              "Handle notifications in foreground and background",
            ],
            code: `// Firebase push example
FirebaseMessaging.onMessage.listen((RemoteMessage message) {
  print('Received message: \${message.notification!.title}');
});`,
            language: "dart"
          },
          {
            heading: "Advanced",
            points: [
              "Send notifications from server via APIs",
              "Use topics and user-specific notifications",
              "Integrate analytics with notifications",
            ],
            code: `// Server-side FCM push example
POST https://fcm.googleapis.com/fcm/send
Headers: Authorization: key=SERVER_KEY
Body: {"to":"/topics/all","notification":{"title":"Hello","body":"Test push"}}`,
            language: "http"
          },
        ],
      },
      {
        name: "Analytics & Monitoring",
        description: "Track user behavior and app performance.",
        fullContent: [
          {
            heading: "Beginner",
            points: [
              "Learn basic analytics concepts",
              "Track simple events (button clicks, page views)",
            ],
            code: `// Flutter Analytics example
FirebaseAnalytics analytics = FirebaseAnalytics();
analytics.logEvent(name: 'button_click');`,
            language: "dart"
          },
          {
            heading: "Intermediate",
            points: [
              "Use Google Analytics or Firebase Analytics for events and user properties",
              "Analyze user flow and retention",
            ],
            code: `// Firebase Analytics setUserProperty
analytics.setUserProperty(name: 'user_type', value: 'premium');`,
            language: "dart"
          },
          {
            heading: "Advanced",
            points: [
              "Combine analytics with A/B testing",
              "Track performance metrics (crash reports, app speed)",
              "Use Crashlytics for monitoring and debugging",
            ],
            code: `// Crashlytics example
FirebaseCrashlytics.instance.log('App loaded successfully');
FirebaseCrashlytics.instance.recordError(error, stackTrace);`,
            language: "dart"
          },
        ],
      },
    ],
  },
];

function AppDevelopment() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            App Development
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {appDevContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      bgcolor: 'rgba(3, 69, 34, 0.1)',
                      color: '#034522',
                      mr: 2,
                      display: 'flex',
                      boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                    }}
                  >
                    {appDevContent[activeTab].icon}
                  </Box>
                  <Typography variant="h5" fontWeight={800} color="#034522">
                    {appDevContent[activeTab].section}
                  </Typography>
                </Box>

                <Box>
                  {appDevContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                              {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.points && (
                              <ul style={{ marginLeft: "1.5rem", marginTop: 0, marginBottom: '16px', color: '#4a5568' }}>
                                {content.points.map((point, pIdx) => (
                                  <li key={pIdx}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language={content.language || "javascript"}
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* 🔹 FEATURED PROJECTS - NEW SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff', mt: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={{ xs: 6, md: 10 }}>
            <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Our Work</Typography>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1, fontSize: { xs: '2rem', md: '3rem' } }}>Featured Mobile Experiences</Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              { title: "FinTech Super App", cat: "React Native", desc: "A unified banking ecosystem integrating crypto wallets, stock trading, and fiat payments.", image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&q=80" },
              { title: "TeleHealth Connect", cat: "Flutter", desc: "HIPAA-compliant video consultation app with real-time vitals monitoring via wearable integration.", image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80" },
              { title: "Urban Logistics", cat: "Native Android (Kotlin)", desc: "Last-mile delivery tracking system with route optimization and offline sync capabilities.", image: "https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?auto=format&fit=crop&w=800&q=80" }
            ].map((project, i) => (
              <Grid item xs={12} md={4} key={i}>
                <motion.div whileHover={{ y: -10 }}>
                  <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #eee', height: '100%' }}>
                    <Box sx={{ height: 250, overflow: 'hidden' }}>
                      <Box component="img" src={project.image} sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: '0.3s', '&:hover': { transform: 'scale(1.1)' } }} />
                    </Box>
                    <Box sx={{ p: 4 }}>
                      <Typography variant="overline" sx={{ color: '#1A9E7F', fontWeight: 700 }}>{project.cat}</Typography>
                      <Typography variant="h5" fontWeight={800} sx={{ color: '#034522', mb: 1.5, mt: 0.5 }}>{project.title}</Typography>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.6}>{project.desc}</Typography>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default AppDevelopment;
